import { useEffect, useMemo, useState } from "react";
import { Blueprint } from "../../../../models/Blueprints";
import { APIEndpoint } from "../../../integrations/versioned-components/types";
import { fetchSimpleAPIEndpoints } from "../../../integrations/utils/IntegrationsAPIClient";
import { getAllStepsForBlueprint, isStepTypeAPIRequest } from "../../utils/BlueprintEditorUtils";

export interface APIEndpointUsage {
  endpointID: string;
  // Undefined if the id isn't in the catalog; the row then falls back to showing the id.
  method: string | undefined;
  path: string | undefined;
  stepIDs: string[];
}

// Step IDs per endpoint ID; exported so the traversal can be unit-tested without the hook.
export const groupStepIDsByEndpointID = (blueprint: Blueprint): Record<string, string[]> => {
  const stepIDsByEndpointID: Record<string, string[]> = {};
  getAllStepsForBlueprint(blueprint)
    .filter(isStepTypeAPIRequest)
    .forEach((step) => {
      const endpointID = step.template?.endpoint;
      if (!endpointID) return;
      stepIDsByEndpointID[endpointID] = [...(stepIDsByEndpointID[endpointID] ?? []), step.id];
    });
  return stepIDsByEndpointID;
};

const useAPIEndpointsUsed = (blueprint: Blueprint) => {
  const [apiEndpointsByID, setAPIEndpointsByID] = useState<Record<string, APIEndpoint>>({});
  const [isLoading, setIsLoading] = useState<boolean>(true);

  // Fetch the integration's endpoint catalog once, to resolve method/path by id.
  useEffect(() => {
    setIsLoading(true);
    fetchSimpleAPIEndpoints({
      integrationID: blueprint.integration.id,
      onSuccess: (data: APIEndpoint[]) => {
        const byID: Record<string, APIEndpoint> = {};
        data.forEach((endpoint) => {
          if (endpoint.id) byID[endpoint.id] = endpoint;
        });
        setAPIEndpointsByID(byID);
      },
      onError: () => {},
      setIsLoading,
    });
  }, [blueprint.integration.id]);

  // Avoid re-walking every step on unrelated context updates (e.g. selecting a step).
  const endpointsUsed = useMemo<APIEndpointUsage[]>(() => {
    const stepIDsByEndpointID = groupStepIDsByEndpointID(blueprint);
    return Object.entries(stepIDsByEndpointID).map(([endpointID, stepIDs]) => ({
      endpointID,
      method: apiEndpointsByID[endpointID]?.method,
      path: apiEndpointsByID[endpointID]?.path,
      stepIDs,
    }));
  }, [blueprint, apiEndpointsByID]);

  return { endpointsUsed, isLoading };
};

export default useAPIEndpointsUsed;
